import { useCallback, useState } from "react"

import styles from "../ConfirmDialog.module.scss"

import ModalDialog from "@/main/components/ui/ModalDialog/ModalDialog"
import { useSouhlAdminMutation } from "@/provider/apollo/Apollo"
import { graphql } from "@/souhl_admin/gql/__generated__"

// --- GraphQL ---

const BulkUpdatePayoutSettlementStatuses = graphql(/* GraphQL */ `
	mutation BulkUpdatePayoutSettlementStatuses_UpdateStatus(
		$input: BulkUpdatePayoutSettlementStatusesInput!
	) {
		bulkUpdatePayoutSettlementStatuses(input: $input) {
			updatedSettlementIds
		}
	}
`)

const BulkUpdateInvoiceSettlementStatuses = graphql(/* GraphQL */ `
	mutation BulkUpdateInvoiceSettlementStatuses_UpdateStatus(
		$input: BulkUpdateInvoiceSettlementStatusesInput!
	) {
		bulkUpdateInvoiceSettlementStatuses(input: $input) {
			updatedSettlementIds
		}
	}
`)

// --- Status transition labels ---

const targetStatusLabels: Record<
	string,
	{
		title: string
		message: (yearMonthStr: string) => string
		confirmLabel: string
	}
> = {
	PENDING: {
		title: "ステータスを「保留中」に戻す",
		message: (ym) =>
			`${ym}の相殺明細のステータスを「保留中」に戻します。よろしいですか？`,
		confirmLabel: "保留中に戻す",
	},
	PAYOUT_IN_PROGRESS: {
		title: "ステータスを「確認中」にする",
		message: (ym) =>
			`${ym}の相殺明細のステータスを「SOUHL 振込確認中」にします。よろしいですか？`,
		confirmLabel: "確認中にする",
	},
	INVOICE_IN_PROGRESS: {
		title: "ステータスを「確認中」にする",
		message: (ym) =>
			`${ym}の相殺明細のステータスを「御社支払い確認中」にします。よろしいですか？`,
		confirmLabel: "確認中にする",
	},
	COMPLETED: {
		title: "ステータスを「完了」にする",
		message: (ym) =>
			`${ym}の相殺明細のステータスを「完了」にします。よろしいですか？`,
		confirmLabel: "完了にする",
	},
	CARRYOVER: {
		title: "ステータスを「来月繰越」にする",
		message: (ym) =>
			`${ym}の相殺明細のステータスを「来月繰越」にします。よろしいですか？`,
		confirmLabel: "来月繰越にする",
	},
}

type Props = {
	onUpdateComplete: () => void
}

type DialogState = {
	settlementId: string
	isPayout: boolean
	targetStatus: string
	yearMonth: { year: number; month: number }
}

export const useUpdateSettlementStatusDialog = ({
	onUpdateComplete,
}: Props) => {
	const [isOpen, setIsOpen] = useState(false)
	const [dialogState, setDialogState] = useState<DialogState | null>(null)
	const [error, setError] = useState<string | null>(null)

	const [bulkUpdatePayout, { loading: payoutLoading }] = useSouhlAdminMutation(
		BulkUpdatePayoutSettlementStatuses,
		{
			context: { disableErrorDialog: true },
		}
	)

	const [bulkUpdateInvoice, { loading: invoiceLoading }] =
		useSouhlAdminMutation(BulkUpdateInvoiceSettlementStatuses, {
			context: { disableErrorDialog: true },
		})

	const loading = payoutLoading || invoiceLoading

	const openDialog = useCallback(
		(
			settlementId: string,
			isPayout: boolean,
			targetStatus: string,
			yearMonth: { year: number; month: number }
		) => {
			setDialogState({ settlementId, isPayout, targetStatus, yearMonth })
			setError(null)
			setIsOpen(true)
		},
		[]
	)

	const handleConfirm = useCallback(() => {
		if (!dialogState) return
		setError(null)

		const variables = {
			input: {
				settlementIds: [dialogState.settlementId],
				targetStatus: dialogState.targetStatus as
					| "PENDING"
					| "PAYOUT_IN_PROGRESS"
					| "INVOICE_IN_PROGRESS"
					| "COMPLETED"
					| "CARRYOVER",
			},
		}

		const mutation = dialogState.isPayout ? bulkUpdatePayout : bulkUpdateInvoice

		mutation({ variables })
			.then(() => {
				setIsOpen(false)
				onUpdateComplete()
			})
			.catch((e: unknown) => {
				let msg = "ステータスの変更に失敗しました。"
				if (e instanceof Error) {
					msg = e.message
				}
				setError(msg)
				console.error("updateSettlementStatus error:", e)
			})
	}, [dialogState, bulkUpdatePayout, bulkUpdateInvoice, onUpdateComplete])

	const render = useCallback(() => {
		const labels = dialogState
			? targetStatusLabels[dialogState.targetStatus]
			: null
		const yearMonthStr = dialogState
			? `${dialogState.yearMonth.year}年${dialogState.yearMonth.month}月`
			: ""

		return (
			<ModalDialog isOpen={isOpen} onOpenChange={setIsOpen}>
				<div className={styles.dialog_content}>
					<h2 className={styles.title}>{labels?.title ?? ""}</h2>
					{error && <p className={styles.error}>{error}</p>}
					<p className={styles.message}>
						{labels?.message(yearMonthStr) ?? ""}
					</p>
					<div className={styles.button_container}>
						<button
							type="button"
							className={styles.cancel_button}
							onClick={() => setIsOpen(false)}
							disabled={loading}
						>
							キャンセル
						</button>
						<button
							type="button"
							className={styles.confirm_button}
							onClick={handleConfirm}
							disabled={loading}
						>
							{loading ? "処理中..." : (labels?.confirmLabel ?? "実行")}
						</button>
					</div>
				</div>
			</ModalDialog>
		)
	}, [isOpen, dialogState, error, loading, handleConfirm])

	return {
		render,
		openDialog,
	}
}
